import type { PropsWithChildren } from "react";

import { CovSensorStatus, FrostFreeStatus } from "@eisox/api/core-api";
import { cn, Popover, Select, Slider, Switch, Typography } from "@eisox/design-system";
import { ChevronDownIcon, HeatIcon, LeafIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { DEFAULT_COMFORT_TEMPERATURE, DEFAULT_NIGHT_TEMPERATURE } from "~/constants/building.constant";
import { m } from "~/paraglide/messages";

interface ParametersSearchProps {
  // Unusual absence detection
  unusualAbsenceDetection?: boolean | null;
  onUnsualAbsenceDetectionChange?: (value: boolean | undefined) => void;
  defaultUnusualAbsenceDetection?: boolean;

  // Precomfort range generation
  precomfortRangeGeneration?: boolean | null;
  onPrecomfortRangeGenerationChange?: (value: boolean | undefined) => void;
  defaultPrecomfortRangeGeneration?: boolean;

  // Button settings
  buttonSettings?: boolean | null;
  onButtonSettingsChange?: (value: boolean | undefined) => void;
  defaultButtonSettings?: boolean;

  // Frost-free mode
  frostFreeMode?: FrostFreeStatus | null;
  onFrostFreeModeChange?: (value: FrostFreeStatus | undefined) => void;
  defaultFrostFreeMode?: FrostFreeStatus;
  frostFreePeriodsAllowed: boolean;

  // Comfort temperature
  comfortTemperature?: number | null;
  onComfortTemperatureChange?: (value: number | undefined) => void;
  defaultComfortTemperature?: number;

  // Night temperature
  nightTemperature?: number | null;
  onNightTemperatureChange?: (value: number | undefined) => void;
  defaultNightTemperature?: number;

  // Sound sensor
  soundSensorParam?: boolean | null;
  onSoundSensorParamChange?: (value: boolean | undefined) => void;
  defaultSoundSensorParam?: boolean;
  showSoundSensorFilter?: boolean;

  // Air quality sensor
  covSensorParam?: CovSensorStatus | null;
  onCovSensorParamChange?: (value: CovSensorStatus | undefined) => void;
  defaultCovSensorParam?: CovSensorStatus;
  showCovSensorFilter?: boolean;

  disabled?: boolean;
  className?: string;
}

const ParametersSearch: React.FC<ParametersSearchProps> = props => {
  const {
    unusualAbsenceDetection: unusualAbsenceDetectionProp,
    onUnsualAbsenceDetectionChange,
    defaultUnusualAbsenceDetection,
    precomfortRangeGeneration: precomfortRangeGenerationProp,
    onPrecomfortRangeGenerationChange,
    defaultPrecomfortRangeGeneration,
    buttonSettings: buttonSettingsProp,
    onButtonSettingsChange,
    defaultButtonSettings,
    frostFreeMode: frostFreeModeProp,
    onFrostFreeModeChange,
    defaultFrostFreeMode,
    frostFreePeriodsAllowed,
    comfortTemperature: comfortTemperatureProp,
    onComfortTemperatureChange,
    defaultComfortTemperature,
    nightTemperature: nightTemperatureProp,
    onNightTemperatureChange,
    defaultNightTemperature,
    soundSensorParam: soundSensorParamProp,
    onSoundSensorParamChange,
    defaultSoundSensorParam,
    showSoundSensorFilter,
    covSensorParam: covSensorParamProp,
    onCovSensorParamChange,
    defaultCovSensorParam,
    showCovSensorFilter,
    disabled,
    className,
  } = props;

  // Props accept `null` to mean "explicitly no filter selected" (controlled mode).
  // `undefined` is reserved for uncontrolled mode only.
  // The onChange callbacks convert null → undefined before notifying the parent filter layer.
  const [unusualAbsenceDetection, setUnusualAbsenceDetection] = useControllableState<boolean | null>({
    prop: unusualAbsenceDetectionProp,
    onChange: value => onUnsualAbsenceDetectionChange?.(value ?? undefined),
    defaultProp: defaultUnusualAbsenceDetection ?? null,
  });
  const [precomfortRangeGeneration, setPrecomfortRangeGeneration] = useControllableState<boolean | null>({
    prop: precomfortRangeGenerationProp,
    onChange: value => onPrecomfortRangeGenerationChange?.(value ?? undefined),
    defaultProp: defaultPrecomfortRangeGeneration ?? null,
  });
  const [buttonSettings, setButtonSettings] = useControllableState<boolean | null>({
    prop: buttonSettingsProp,
    onChange: value => onButtonSettingsChange?.(value ?? undefined),
    defaultProp: defaultButtonSettings ?? null,
  });
  const [frostFreeMode, setFrostFreeMode] = useControllableState<FrostFreeStatus | null>({
    prop: frostFreeModeProp,
    onChange: value => onFrostFreeModeChange?.(value ?? undefined),
    defaultProp: defaultFrostFreeMode ?? null,
  });
  const [comfortTemperature, setComfortTemperature] = useControllableState<number | null>({
    prop: comfortTemperatureProp,
    onChange: value => onComfortTemperatureChange?.(value ?? undefined),
    defaultProp: defaultComfortTemperature ?? null,
  });
  const [nightTemperature, setNightTemperature] = useControllableState<number | null>({
    prop: nightTemperatureProp,
    onChange: value => onNightTemperatureChange?.(value ?? undefined),
    defaultProp: defaultNightTemperature ?? null,
  });
  const [soundSensorParam, setSoundSensorParam] = useControllableState<boolean | null>({
    prop: soundSensorParamProp,
    onChange: value => onSoundSensorParamChange?.(value ?? undefined),
    defaultProp: defaultSoundSensorParam ?? null,
  });
  const [covSensorParam, setCovSensorParam] = useControllableState<CovSensorStatus | null>({
    prop: covSensorParamProp,
    onChange: value => onCovSensorParamChange?.(value ?? undefined),
    defaultProp: defaultCovSensorParam ?? null,
  });

  // Handlers pass null (not undefined) to keep the setter in controlled mode.
  // The onChange wrapper above converts null → undefined for the parent filter layer.
  const handleChangeUnsualAbsenceDetection = (value: boolean | null) => {
    setUnusualAbsenceDetection(value);
  };
  const handleChangePrecomfortRangeGeneration = (value: boolean | null) => {
    setPrecomfortRangeGeneration(value);
  };
  const handleChangeButtonSettings = (value: boolean | null) => {
    setButtonSettings(value);
  };
  const handleChangeFrostFreeMode = (value: string) => {
    if (!value) setFrostFreeMode(null);
    else setFrostFreeMode(value as FrostFreeStatus);
  };
  const handleChangeSoundSensorParam = (value: boolean | null) => {
    setSoundSensorParam(value);
  };
  const handleChangeCovSensorParam = (value: string) => {
    if (!value) setCovSensorParam(null);
    else setCovSensorParam(value as CovSensorStatus);
  };

  const getBooleanLabel = (value: boolean | null | undefined) => {
    if (value == null) return m.unselected();
    return value ? m.activate() : m.deactivate();
  };

  const PARAMETER_VALUE_LABELS = {
    unusualAbsenceDetection: m.unsual_absence(),
    precomfortRangeGeneration: m.pre_comfort(),
    buttonSettings: m.buttons(),
    frostFreeMode: m.frost_free(),
    comfortTemperature: m.comfort(),
    nightTemperature: m.night(),
    soundSensorParam: m.noise(),
    covSensorParam: m.air(),
  } as const;

  const value = Object.entries({
    unusualAbsenceDetection,
    precomfortRangeGeneration,
    buttonSettings,
    frostFreeMode,
    comfortTemperature,
    nightTemperature,
    soundSensorParam,
    covSensorParam,
  })
    .filter(([_, value]) => value != null)
    .map(([key, _]) => PARAMETER_VALUE_LABELS[key as keyof typeof PARAMETER_VALUE_LABELS]);

  return (
    <Popover.Root>
      <Popover.Trigger
        className={cn(
          "flex cursor-pointer items-center justify-between rounded-[12px] border border-primary-darker-gray bg-primary-white px-[20px] py-[10px] disabled:cursor-default disabled:bg-primary-gray",
          className,
        )}
        disabled={disabled}
      >
        <Typography
          className={cn(
            "font-normal text-base leading-[21px]",
            value.length === 0 && "font-light text-primary-dark-gray italic",
          )}
        >
          {value.length > 0 ? value.join(", ") : m.settings()}
        </Typography>
        <ChevronDownIcon />
      </Popover.Trigger>
      <Popover.Content className={cn("flex flex-col gap-[10px]")} align="start" sideOffset={5}>
        <Parameter
          name={
            <>
              {m.unusual_absence_detection()}
              <LeafIcon style={{ color: colors.primary.green }} />
            </>
          }
          label={getBooleanLabel(unusualAbsenceDetection)}
        >
          <Switch
            forceThreeState
            checked={unusualAbsenceDetection ?? null}
            onCheckedChange={handleChangeUnsualAbsenceDetection}
          />
        </Parameter>
        <Parameter
          name={
            <>
              {m.pre_comfort_timeslot_generation()}
              <HeatIcon style={{ color: colors.planning.comfort }} />
            </>
          }
          label={getBooleanLabel(precomfortRangeGeneration)}
        >
          <Switch
            forceThreeState
            checked={precomfortRangeGeneration ?? null}
            onCheckedChange={handleChangePrecomfortRangeGeneration}
          />
        </Parameter>
        <Parameter name={m.button_settings()} label={getBooleanLabel(buttonSettings)}>
          <Switch
            forceThreeState
            checked={buttonSettings ?? null}
            onCheckedChange={handleChangeButtonSettings}
            disabled={disabled}
          />
        </Parameter>
        <div className={"gap-2.5 flex flex-col text-base"}>
          <div className={"gap-1.25 font-semibold flex items-center text-primary-blue"}>{m.frost_free_mode()}</div>
          <div className={"mt-1"}>
            <Select.Root multiple={false} value={frostFreeMode ?? ""} onValueChange={handleChangeFrostFreeMode}>
              <Select.Trigger disabled={disabled} className={"w-full"}>
                <Select.Value placeholder={m.unselected()} />
              </Select.Trigger>
              <Select.Content>
                <Select.Viewport>
                  <Select.Item value="">{m.unselected()}</Select.Item>
                  <Select.Item value={FrostFreeStatus.ENABLED}>{m.activate()}</Select.Item>
                  <Select.Item value={FrostFreeStatus.DISABLED}>{m.deactivate()}</Select.Item>
                  {frostFreePeriodsAllowed && (
                    <Select.Item value={FrostFreeStatus.PERIODS}>{m.configured_by_periods()}</Select.Item>
                  )}
                </Select.Viewport>
              </Select.Content>
            </Select.Root>
          </div>
        </div>
        <Slider
          label={`${m.comfort_temperature()} (°C)`}
          min={0}
          max={30}
          step={0.5}
          defaultValue={DEFAULT_COMFORT_TEMPERATURE}
          value={comfortTemperature ?? undefined}
          onValueChange={v => setComfortTemperature(v ?? null)}
          displayButtons={false}
          undefinedValue
        />
        <Slider
          label={`${m.night_temperature()} (${m.room_type({ type: "Room" })} °C)`}
          min={0}
          max={30}
          step={0.5}
          defaultValue={DEFAULT_NIGHT_TEMPERATURE}
          value={nightTemperature ?? undefined}
          onValueChange={v => setNightTemperature(v ?? null)}
          displayButtons={false}
          undefinedValue
        />
        {showSoundSensorFilter && (
          <Parameter name={m.sound_sensor()} label={getBooleanLabel(soundSensorParam)}>
            <Switch forceThreeState checked={soundSensorParam ?? null} onCheckedChange={handleChangeSoundSensorParam} />
          </Parameter>
        )}
        {showCovSensorFilter && (
          <div className={"gap-2.5 flex flex-col text-base"}>
            <div className={"gap-1.25 font-semibold flex items-center text-primary-blue"}>{m.air_sensor()}</div>
            <div className={"mt-1"}>
              <Select.Root multiple={false} value={covSensorParam ?? ""} onValueChange={handleChangeCovSensorParam}>
                <Select.Trigger className={"w-full"}>
                  <Select.Value placeholder={m.unselected()} />
                </Select.Trigger>
                <Select.Content>
                  <Select.Viewport>
                    <Select.Item value="">{m.unselected()}</Select.Item>
                    <Select.Item value={CovSensorStatus.ENABLED}>{m.activate()}</Select.Item>
                    <Select.Item value={CovSensorStatus.DISABLED}>{m.deactivate()}</Select.Item>
                    <Select.Item value={CovSensorStatus.PERIODS}>{m.activate_by_timeslot()}</Select.Item>
                    <Select.Item value={CovSensorStatus.PRESENCE}>{m.activate_on_presence_timeslot()}</Select.Item>
                  </Select.Viewport>
                </Select.Content>
              </Select.Root>
            </div>
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

ParametersSearch.displayName = "ParametersSearch";

interface ParameterProps extends PropsWithChildren {
  name: React.ReactNode;
  label: React.ReactNode;
}

const Parameter: React.FC<ParameterProps> = ({ name, label, children }) => (
  <div className={cn("flex flex-col gap-[5px] text-base")}>
    <div className={cn("font-semibold flex items-center gap-[5px] text-primary-blue")}>{name}</div>
    <label className={cn("font-medium flex justify-between italic")}>
      {label}
      {children}
    </label>
  </div>
);

export { ParametersSearch };
export type { ParametersSearchProps };
